import { DateTimeInfo } from "../../DateTimeInfo";
import { PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean } from "../../RadObject";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { CopyValueToClipboardMixin, ICopyValueToClipboardMixin } from "./Mixins/CopyValueToClipboardMixin";
import { NAMESPACE } from "./_Namespace";

import "./Text.scss";

export type TextPropertyValues =
    InputControlPropertyValues
    & {
        preserveLineBreaks: boolean;
    };

export type TextProperties = {
    [name in keyof TextPropertyValues]: Property<TextPropertyValues[name]>
};

export interface IText extends IInputControl, TextProperties, ICopyValueToClipboardMixin
{
}

export class Text extends InputControl implements IText
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Text";
    public static override className = "Text";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TextPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                elementType: "p",
                text: StringHelper.empty,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _valueElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public preserveLineBreaks!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TextPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-text");

        // Trigger Property Changes
        this.preserveLineBreaks.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _preserveLineBreaksChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-feature-preserve-line-breaks");
        else
            this.removeCssClass("rad-feature-preserve-line-breaks");
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TextPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._valueElementRef = new ElementRef();

        this.preserveLineBreaks = new PropertyBoolean(this, propertyValues.preserveLineBreaks, this._preserveLineBreaksChanged);
    }

    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        this.labelTargetId = this.controlId + "-span";

        return (
            <p>
                <span ref={this._valueElementRef} id={this.labelTargetId}></span>
            </p>
        );
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);

        let
            text = change.currentValue;

        if (text)
        {
            if (DateTimeInfo.isDateString(text))
                text = DateTimeInfo.fromDateString(text).toLocal().toString("dd/MM/yyyy HH:mm:ss");
        }

        this._valueElementRef.current.text(text);
    }
    protected override _valueChanged(change: PropertyChange<any>)
    {
        super._valueChanged(change);
        this.text.set(change.currentValue);
    }

    // #endregion

    // #region ICopyValueToClipboardMixin

    public copyValueToClipboardAsync(): Promise<void>
    {
        return CopyValueToClipboardMixin.copyValueToClipboardAsync(this);
    }

    // #endregion

    // #endregion
}

